<template>
  <Form as="div" ref="form">
    <div v-if="questions">
      <h1 v-html="memberEligibilityModel.title" />
      <div class="push-down" v-html="memberEligibilityModel.description" />

      <div
        v-for="(questionObject, key) in questions.filter((question) => question.isVisible)"
        :key="key"
        class="push-down"
      >
        <eligibility-question
          v-model="questionObject.value"
          :index-key="key"
          :question="questionObject"
          :validation-text="memberEligibilityModel.questionValidationText"
        />
      </div>
      <info-box
        v-if="hasBrandAffinityMatch && !isStudent"
        :text="memberEligibilityString"
        data-test-brand-match-info-box
        info-box-class="info-box-no-arrow info-box-check"
      />
      <info-box
        v-if="noResultAfterAllQuestions"
        :text="memberEligibilityModel.noResultMessage"
        data-test-no-result-info-box
        info-box-class="info-box-no-arrow"
      />
      <info-box
        v-if="isStudent"
        :text="memberEligibilityModel.isStudentMessage"
        data-test-student-info-box
        info-box-class="info-box-no-arrow"
      />
    </div>
  </Form>
</template>

<script>
import EligibilityQuestion from '../components/EligibilityQuestion.vue';
import InfoBox from '@/components/UI/InfoBox.vue';
import Utility from '@/common/UtilityFunctions.js';
import axios from 'axios';
import { mapState } from 'vuex';
import { isEqual } from 'lodash';

export default {
  name: 'MemberEligibility',
  components: {
    EligibilityQuestion,
    InfoBox
  },
  async beforeRouteLeave(to, from, next) {
    if (this.currentBrandMatch && to.path === this.$store.state.createMember.stepThreePath) {
      this.$emit('component-mounted', 3);
      return (document.location.href = this.affinityObject.redirectComponentModel.link.url);
    }
    if (to.path === '/url') {
      return (document.location.href = this.buttons.nextButton.redirectTo);
    }
    if (to.path === this.$store.state.createMember.stepOnePath) {
      this.$store.commit('createMember/setQuestions', this.questions);
      return next();
    }

    const questionChanged =
      this.questionChangeIndex !== null ? this.questions[this.questionChangeIndex] : null;

    await this.$nextTick();

    await this.$refs.form.validate();
    this.errors = this.$refs.form.getErrors();
    let hasErrors = Object.keys(this.errors).length > 0;

    if (!hasErrors && this.hasBrandAffinityMatch) {
      axios
        .get(
          '/api/eligibility/gettransferdata?viewModelDatasourceId=' +
            this.memberEligibilityModel.viewmodelDatasourceID
        )
        .then((response) => {
          this.$store.commit('createMember/setRedirectTransferData', response.data);
        })
        .catch((err) => {
          Utility.logError(err, this);
        });
      this.$store.commit('createMember/setQuestions', this.questions);
      next();
    }

    let nextFalse = false;
    if (!hasErrors && !this.hasBrandAffinityMatch) {
      this.$store.commit('createMember/setQuestions', this.questions);
      this.questions[this.questionChangeIndex + 1].isVisible = true;
      nextFalse = true;
    }

    if (
      questionChanged !== null &&
      questionChanged.value !== null &&
      questionChanged.value.value === 'INVALID'
    ) {
      this.questions[this.questionChangeIndex + 1].isVisible = true;
      nextFalse = true;
    }

    if (nextFalse) next(false);
  },
  data() {
    return {
      questions: this.questionsFromStore
    };
  },
  computed: {
    ...mapState({
      memberEligibilityModel: (state) => state.createMember.vueModel.stepTwoMemberQuestions,
      eligibilityResultModel: (state) => state.createMember.vueModel.stepThreeBrandRedirection,
      questionsFromStore: (state) => state.createMember.vueModel.stepTwoMemberQuestions.questions,
      hasBrandAffinityMatch: (state) => state.createMember.hasBrandAffinityMatch,
      questionChangeIndex: (state) => state.createMember.questionChangeIndex,
      nextButtonLabel: (state) => state.createMember.vueModel.sharedFields.nextButtonLabel,
      nextQuestionButtonLabel: (state) =>
        state.createMember.vueModel.sharedFields.nextQuestionButtonLabel,
      backButtonLabel: (state) => state.createMember.vueModel.sharedFields.backButtonLabel,
      stepOnePath: (state) => state.createMember.stepOnePath,
      stepThreePath: (state) => state.createMember.stepThreePath,
      buttons: (state) => state.createMember.buttons,
      isStudent: (state) => state.createMember.isStudent,
      noResultAfterAllQuestions: (state) => state.createMember.noResultAfterAllQuestions,
      currentBrandMatch: (state) => state.createMember.currentBrandMatch
    }),
    questionsStringified() {
      return JSON.stringify(this.questions);
    },
    changedQuestion() {
      const question = this.questions[this.questionChangeIndex];
      return typeof question === 'undefined' ? null : question;
    },
    isApiSearch() {
      return this.changedQuestion.type === 2;
    },
    isDropdown() {
      return this.changedQuestion.type === 3;
    },
    isRadio() {
      return this.changedQuestion.type === 1;
    },
    affinityId() {
      if (this.changedQuestion === null) {
        return null;
      }

      return this.changedQuestion.value.value;
    },
    affinityObject() {
      if (this.affinityId === null) {
        return null;
      }
      const availableBrands = this.eligibilityResultModel.availableBrands;
      const result = availableBrands.find(
        (element) => element.brandAffinity.toString() === this.affinityId
      );

      if (typeof result === 'undefined') {
        return null;
      }

      return result;
    },
    memberEligibilityString() {
      if (this.affinityObject === null) {
        return '';
      }

      return this.affinityObject.redirectComponentModel.availabilityReasonText;
    }
  },
  watch: {
    questionsStringified(val, oldVal) {
      if (oldVal === undefined) {
        return;
      }

      let newValue = JSON.parse(val);
      let oldValue = JSON.parse(oldVal);
      newValue.forEach((element, index) => {
        if (!isEqual(element.value, oldValue[index].value) && element.value !== null) {
          this.$store.commit('createMember/setQuestionChangeIndex', index);
          this.questionChange();
          return false;
        }
      });
      this.$store.commit('createMember/setQuestions', this.questions);
    },
    hasBrandAffinityMatch(val) {
      const prevButtonPath = !val ? null : this.stepOnePath;
      const nextButtonLabel = val ? this.nextButtonLabel : this.nextQuestionButtonLabel;
      const nextButtonClass = val ? 'primary' : 'secondary';

      this.$store.dispatch('createMember/setButtons', {
        prevButton: {
          path: prevButtonPath,
          class: 'tertiary',
          label: this.backButtonLabel,
          redirectTo: null
        },
        nextButton: {
          path: this.stepThreePath,
          class: nextButtonClass,
          label: nextButtonLabel,
          redirectTo: null
        }
      });
    },
    noResultAfterAllQuestions(val) {
      this.hideNavigationButtons(val);
    },
    isStudent(val) {
      this.hideNavigationButtons(val);
    }
  },
  mounted() {
    this.questions = this.questionsFromStore;
    this.setButtonsOnMounted();
    this.$emit('component-mounted', 2);
    Utility.scrollToTop();
  },
  methods: {
    resetSubsequentQuestions() {
      this.questions.forEach((element, index) => {
        if (index > this.questionChangeIndex) {
          element.isVisible = false;
          element.value = null;
        }
      });
    },
    questionChange() {
      const currentBrandMatched = this.affinityId === this.memberEligibilityModel.currentBrandId;
      this.$store.commit('createMember/setCurrentBrandMatch', currentBrandMatched);

      this.$store.commit('createMember/setNoResultAfterAllQuestions', false);
      this.$store.commit('createMember/setIsStudent', false);

      if (this.changedQuestion.value === null) {
        this.resetSubsequentQuestions();
        return;
      }

      const isLastQuestion = this.questionChangeIndex === this.questions.length - 1;
      const hasAffinityMatch = this.affinityObject !== null;
      this.$store.commit(
        'createMember/setAffinityMatchedQuestion',
        hasAffinityMatch ? this.changedQuestion : null
      );

      const isStudent = this.questions[this.questionChangeIndex].value.isStudent;
      this.$store.commit(
        'createMember/setIsStudent',
        typeof isStudent === 'undefined' ? false : isStudent
      );
      if (isStudent) {
        this.$store.commit(
          'createMember/setStudentInsuranceLink',
          this.affinityObject.redirectComponentModel.studentInsuranceLink
        );
        this.hideNavigationButtons();
      }

      if (isLastQuestion && this.affinityObject === null) {
        this.$store.commit('createMember/setNoResultAfterAllQuestions', true);
        this.hideNavigationButtons();
      }

      if (this.affinityObject === null) {
        this.$store.commit('createMember/setHasBrandAffinityMatch', hasAffinityMatch);
        this.resetSubsequentQuestions();
        this.showNextQuestion();
        return;
      }

      axios
        .post('/api/eligibility/submittypeselection', null, {
          params: {
            memberGroupReferenceDataItemId: this.changedQuestion.value.memberGroupSelectorId
          }
        })
        .catch((err) => {
          Utility.logError(err, this);
        });

      this.$store.commit('createMember/setHasBrandAffinityMatch', hasAffinityMatch);
      this.$store.commit('createMember/setAffinityId', this.affinityId);
      this.resetSubsequentQuestions();
    },
    async showNextQuestion() {
      if (this.questionChangeIndex === this.questions.length - 1) {
        return;
      }
      this.$refs.form.validate();

      if ((this.isRadio || this.isDropdown) && !this.hasBrandAffinityMatch) {
        this.questions[this.questionChangeIndex + 1].isVisible = true;
      }
    },
    setButtonsOnMounted() {
      const prevButtonPath =
        this.isStudent || this.noResultAfterAllQuestions || !this.hasBrandAffinityMatch
          ? null
          : this.stepOnePath;
      const nextButtonPath =
        this.isStudent || this.noResultAfterAllQuestions ? null : this.stepThreePath;
      const nextButtonLabel = this.hasBrandAffinityMatch
        ? this.nextButtonLabel
        : this.nextQuestionButtonLabel;
      const nextButtonClass = this.hasBrandAffinityMatch ? 'primary' : 'secondary';

      this.$store.dispatch('createMember/setButtons', {
        prevButton: {
          path: prevButtonPath,
          class: 'tertiary',
          label: this.backButtonLabel,
          redirectTo: null
        },
        nextButton: {
          path: nextButtonPath,
          class: nextButtonClass,
          label: nextButtonLabel,
          redirectTo: null
        }
      });
    },
    hideNavigationButtons(val) {
      if (!val) {
        return;
      }
      this.$store.dispatch('createMember/setButtons', {
        prevButton: {
          path: null,
          class: null,
          label: null,
          redirectTo: null
        },
        nextButton: {
          path: null,
          class: null,
          label: null,
          redirectTo: null
        }
      });
    }
  }
};
</script>
